import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import { ArcheLayout, ArcheDisplayMode } from '../components/ArcheLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { MakeARequestPage } from './make-a-request';
import { SingleAIPage } from './single-ai';
import { ImageGeneratorArchePage } from './image-generator';
import { MultiAIPage } from './multi-ai';
import { LearnAboutFiasChatPage } from './learn-fias-chat';
import { LearnAnythingPage } from './learn-anything';
import { ContactBookArchePage } from './contact-book';
import { ChatArchePage } from './chat';
import { GuildArchePage } from './guilds';
import { DfeArchePage } from './dfe';
import { DfeInventoryTransitionArchePage } from './dfe-inventory-transition';
import { FavoritesArchePage } from './favorites';
import { GameRewardsArchePage } from './game-rewards';
import { DfeGameStateArchePage } from './dfe-game-state';
import { PollsArchePage } from './polls';
import { PluginBuilderArchePage } from './plugin-builder';
import { CraftingArchePage } from './crafting';
import { MiningArchePage } from './mining';
import { LearnAboutFiasArchePage } from './learn-about-fias';
import { LiquidityCachesArchePage } from './liquidity-caches';
import { FiardleArchePage } from './fiardle';
import { PluginRenderer } from '../components/PluginRenderer';
import { CalendarArchePage } from './calendar';
import { SocialMediaArchePage } from './social-media';
import { IconGeneratorArchePage } from './icon-generator';
import { DfeAvatarsArchePage } from './dfe-avatars';
import { DfeIconsArchePage } from './dfe-icons';
import { LitcraftAddressablesArchePage } from './litcraft-addressables';
import { LitLegionArchePage } from './lit-legion';
import { BusinessArchePage } from './businesses';
import { ImageEditorArchePage } from './image-editor';
import { BetaAccessArchePage } from './beta-access';
import { useAuth } from '@/auth/hooks/useAuth';
import api, { ArcheMetadata } from '@/lib/api';

/**
 * Determines the display mode for the arche layout based on metadata, auth state, and embed flag.
 *
 * Security: Embed mode is only allowed for arches that are:
 * - Public (isPublic=true)
 * - Read-only (no workflow)
 * This prevents authenticated actions from being performed in embedded iframes.
 */
function determineDisplayMode(
  metadata: ArcheMetadata | null,
  isAuthenticated: boolean,
  isEmbed: boolean,
): ArcheDisplayMode {
  // Embed mode is only allowed for embeddable arches (public + no workflow)
  // This is a security measure to prevent authenticated actions in iframes
  if (isEmbed && metadata?.isEmbeddable) {
    return 'embed';
  }

  if (!metadata) {
    return 'normal'; // Default to normal while loading
  }

  // Full-screen mode takes precedence
  if (metadata.isFullScreen) {
    return 'fullscreen';
  }

  // Public mode for unauthenticated users on public arches
  if (metadata.isPublic && !isAuthenticated) {
    return 'public';
  }

  return 'normal';
}

/**
 * Error component shown when embed mode is requested but not allowed
 */
function EmbedNotAllowedError() {
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <Card className="max-w-md mx-4">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Embedding Not Allowed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            This arche cannot be embedded. Only public, read-only arches can be embedded in iframes.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Generic arche page for dynamic arches (plugins, etc.)
 */
function GenericArchePage({ archeId }: { archeId: string }) {
  const { isAuthenticated, signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const [metadata, setMetadata] = useState<ArcheMetadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(true);

  // Check for embed mode via query parameter
  const isEmbed = searchParams.get('embed') === 'true';

  // Security: Clear any existing authentication when in embed mode
  // This prevents authenticated actions from being performed in embedded iframes
  useEffect(() => {
    if (isEmbed && isAuthenticated) {
      signOut();
    }
  }, [isEmbed, isAuthenticated, signOut]);

  useEffect(() => {
    let cancelled = false;

    async function fetchMetadata() {
      try {
        const result = await api.getArcheMetadata(archeId);
        if (!cancelled) {
          setMetadata(result);
        }
      } catch {
        if (!cancelled) {
          setMetadata(null);
        }
      } finally {
        if (!cancelled) {
          setMetadataLoading(false);
        }
      }
    }

    fetchMetadata();

    return () => {
      cancelled = true;
    };
  }, [archeId]);

  // Check if embed mode was requested but not allowed
  const embedDenied = isEmbed && !metadataLoading && metadata && !metadata.isEmbeddable;

  if (embedDenied) {
    return <EmbedNotAllowedError />;
  }

  // Plugin arches render in a sandboxed iframe
  // Use metadata.archId (the resolved real ID) instead of the URL param which may be a slug
  if (!metadataLoading && metadata?.renderMode === 'plugin') {
    const displayMode = determineDisplayMode(metadata, isAuthenticated, isEmbed);
    return (
      <ArcheLayout displayMode={displayMode}>
        <PluginRenderer archeId={metadata.archId} />
      </ArcheLayout>
    );
  }

  // Non-plugin generic arches — show "not available"
  if (!metadataLoading) {
    return (
      <ArcheLayout displayMode="normal">
        <div className="p-8 max-w-3xl mx-auto">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Not Available
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">This arche is not available.</p>
            </CardContent>
          </Card>
        </div>
      </ArcheLayout>
    );
  }

  return null;
}

export function ArcheInteractionPage() {
  const { id: archeId } = useParams<{ id: string }>();

  if (!archeId) {
    return <Navigate to="/a/arche_make_a_request" replace />;
  }

  // Redirects
  if (archeId === 'arche_marketplace') {
    return <Navigate to="/marketplace" replace />;
  }
  // Internal arches with dedicated page components
  if (archeId === 'arche_guilds') {
    return <GuildArchePage />;
  }
  if (archeId === 'arche_chat') {
    return <ChatArchePage />;
  }
  if (archeId === 'arche_make_a_request') {
    return <MakeARequestPage />;
  }
  if (archeId === 'arche_single_ai') {
    return <SingleAIPage />;
  }
  if (archeId === 'arche_image_generator') {
    return <ImageGeneratorArchePage />;
  }
  if (archeId === 'arche_multi_ai') {
    return <MultiAIPage />;
  }
  if (archeId === 'arche_learn_fias_chat') {
    return <LearnAboutFiasChatPage />;
  }
  if (archeId === 'arche_learn_anything') {
    return <LearnAnythingPage />;
  }
  if (archeId === 'arche_dfe') {
    return <DfeArchePage />;
  }
  if (archeId === 'arche_dfe_inventory_transition') {
    return <DfeInventoryTransitionArchePage />;
  }
  if (archeId === 'arche_contact_book') {
    return <ContactBookArchePage />;
  }
  if (archeId === 'arche_favorites') {
    return <FavoritesArchePage />;
  }
  if (archeId === 'arche_game_rewards') {
    return <GameRewardsArchePage />;
  }
  if (archeId === 'arche_dfe_game_state') {
    return <DfeGameStateArchePage />;
  }
  if (archeId === 'arche_polls') {
    return <PollsArchePage />;
  }
  if (archeId === 'arche_plugin_builder') {
    return <PluginBuilderArchePage />;
  }
  if (archeId === 'arche_crafting') {
    return <CraftingArchePage />;
  }
  if (archeId === 'arche_dfe_mining') {
    return <MiningArchePage />;
  }
  if (archeId === 'arche_learn_about_fias') {
    return <LearnAboutFiasArchePage />;
  }
  if (archeId === 'arche_liquidity_caches') {
    return <LiquidityCachesArchePage />;
  }
  if (archeId === 'arche_fiardle') {
    return <FiardleArchePage />;
  }
  if (archeId === 'arche_calendar') {
    return <CalendarArchePage />;
  }
  if (archeId === 'arche_social_media') {
    return <SocialMediaArchePage />;
  }
  if (archeId === 'arche_icon_generator') {
    return <IconGeneratorArchePage />;
  }
  if (archeId === 'arche_lit_legion') {
    return <LitLegionArchePage />;
  }

  if (archeId === 'arche_businesses') {
    return <BusinessArchePage />;
  }

  if (archeId === 'arche_image_editor') {
    return <ImageEditorArchePage />;
  }

  if (archeId === 'arche_beta_access') {
    return <BetaAccessArchePage />;
  }

  if (archeId === 'arche_dfe_avatars') {
    return <DfeAvatarsArchePage />;
  }

  if (archeId === 'arche_dfe_icons') {
    return <DfeIconsArchePage />;
  }

  if (archeId === 'arche_litcraft_addressables') {
    return <LitcraftAddressablesArchePage />;
  }

  // All other arches (plugins, etc.) use the generic page
  return <GenericArchePage archeId={archeId} />;
}

export default ArcheInteractionPage;
